import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import usePost from "../../hooks/usePost";
import usePut from "../../hooks/usePut";
import { ADMINISTRATOR } from "../../routes/routes_name";

const UseFormCreateUser = (props) => {
    const {
        isLoading,
        error,
        isSuccess,
        navigate,
        handlePost,
    } = usePost();
    const {
        isLoading: isLoadingPut,
        error: errorPut,
        isSuccess: isSuccessPut,
        navigate: navigatePut,
        handlePut,
    } = usePut();

    const location = useLocation();
    const [form, setForm] = useState({});
    const roleDefault = "superadmin";

    const defaultFeatures = useMemo(() => ({
        all: false,
        dashboard: false,
        fabrics: false,
        style_accents: false,
        report: false,
        settings: false,
        master: {
            administrator: false,
            user: false,
            product_category: false,
            product_list: false,
        },
        transactions: false,
    }), []);

    useEffect(() => {
        console.log('Location state received:', location.state);
        console.log('Default features:', defaultFeatures);
        
        if (props.type === 'post') {
            setForm({
                name: '',
                email: '',
                password: '',
                phone_number: '',
                role: roleDefault,
                features_access_user: { ...defaultFeatures },
            });
        } else {
            const mergedFeatures = {
                ...defaultFeatures,
                ...(location.state?.features_access_user || {}),
                master: {
                    ...defaultFeatures.master,
                    ...(location.state?.features_access_user?.master || {}),
                },
            };
            console.log('Merged features for edit:', mergedFeatures);
            
            setForm({
                id: location.state?._id ?? '',
                name: location.state?.name ?? '',
                email: location.state?.email ?? '',
                password: location.state?.password ?? '',
                phone_number: location.state?.phone_number ?? '',
                role: location.state?.role ?? roleDefault,
                features_access_user: mergedFeatures,
            });
        }
    }, [props.type, location.state, roleDefault, defaultFeatures]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const data = {
            name: form['name'],
            email: form['email'],
            password: form['password'],
            phone_number: form['phone_number'],
            role: form['role'],
            features_access_user: form['features_access_user'],
        };

        console.log('Form data being submitted:', data);

        let params = {
            url: "/v1/user/create-admin",
            form: data,
            navigation: ADMINISTRATOR,
            titleSuccess: 'Saved!',
            descSuccess: 'Administrator data has been successfully created.',
            type: 'application/json'
        };
        
        if (props.type === "post") {
            handlePost(params);
        } else {
            params.titleSuccess = 'Edited!';
            params.descSuccess = 'Administrator data has been successfully edited.';
            params.url = `/v1/user/edit-superadmin?_id=${form["id"]}`;
            handlePut(params);
        }
    };

    const handleChange = (props) => {
        const { id, value, checked, type } = props.target;

        if (id.startsWith('features_access_user')) {
            const path = id.split('.');
            const featureValue = type === 'checkbox' ? checked : value;

            setForm((prev) => {
                const currentFeatures = prev.features_access_user || defaultFeatures;
                
                if (path.length === 2) {
                    const featureKey = path[1];

                    if (featureKey === 'all') {
                        const toggled = Boolean(featureValue);
                        const updatedMaster = Object.keys(currentFeatures.master || {}).reduce((acc, key) => ({
                            ...acc,
                            [key]: toggled,
                        }), {});

                        const updatedAll = {
                            all: toggled,
                            dashboard: toggled,
                            fabrics: toggled,
                            style_accents: toggled,
                            report: toggled,
                            settings: toggled,
                            transactions: toggled,
                            master: updatedMaster,
                        };

                        return {
                            ...prev,
                            features_access_user: updatedAll,
                        };
                    }

                    const updatedFeatures = {
                        ...currentFeatures,
                        [featureKey]: Boolean(featureValue),
                    };

                    const everyEnabled = [
                        updatedFeatures.dashboard,
                        updatedFeatures.fabrics,
                        updatedFeatures.style_accents,
                        updatedFeatures.report,
                        updatedFeatures.settings,
                        updatedFeatures.transactions,
                        ...Object.values(updatedFeatures.master || {}),
                    ].every(Boolean);

                    return {
                        ...prev,
                        features_access_user: {
                            ...updatedFeatures,
                            all: everyEnabled,
                        },
                    };
                }

                if (path.length === 3 && path[1] === 'master') {
                    const masterKey = path[2];
                    const currentMaster = currentFeatures.master || {};
                    
                    const updatedMaster = {
                        ...currentMaster,
                        [masterKey]: Boolean(featureValue),
                    };

                    const updatedFeatures = {
                        ...currentFeatures,
                        master: updatedMaster,
                    };

                    const everyEnabled = [
                        updatedFeatures.dashboard,
                        updatedFeatures.fabrics,
                        updatedFeatures.style_accents,
                        updatedFeatures.report,
                        updatedFeatures.settings,
                        updatedFeatures.transactions,
                        ...Object.values(updatedMaster),
                    ].every(Boolean);

                    return {
                        ...prev,
                        features_access_user: {
                            ...updatedFeatures,
                            all: everyEnabled,
                        },
                    };
                }

                return prev;
            });

            return;
        }

        setForm((prev) => ({
            ...prev,
            [id]: type === 'checkbox' ? checked : value,
        }));
    };

    useEffect(() => {
        console.log('Current form state:', form);
    }, [form]);

    return {
        isLoading,
        isLoadingPut,
        form,
        handleChange,
        handleSubmit
    };
};

export default UseFormCreateUser;
